import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { settingsAdminApi } from '@carrent/api'
import { useAuthStore } from '@carrent/core'
import { PageHeader, Icon, LegalConsent, Markdown, MarkdownToolbar, handleToolbarKeydown } from '@carrent/ui'
import { resetLegalCache, formatDateTime } from '@carrent/domain'
import VideoSettingsSection from './VideoSettingsSection'

/**
 * Настройки владельца.
 *
 * Разделы разложены по вкладкам в браузерной манере: вкладки стоят
 * поверх панели и активная сливается с ней. Закрыть вкладку нельзя —
 * это разделы настроек, а не документы. Активная вкладка живёт
 * в ?tab=, чтобы обновление страницы и ссылка коллеге вели туда же.
 *
 * Правовые документы хранятся текстом, а не ссылкой: внешний домен
 * живёт своей жизнью, и пользователь не должен соглашаться со
 * страницей 404. Полотно правится в отдельном окне с предпросмотром —
 * как статья базы знаний, но без черновиков: документ сохраняется
 * вместе с остальной формой одним PUT.
 *
 * Доступ только у роли admin. Менеджер региона ведёт операционную
 * работу; оферта — не его зона ответственности.
 */

const TABS = [
  { key: 'legal', label: 'Правовые документы', icon: 'file' },
  { key: 'video', label: 'Видеозвонки', icon: 'camera' },
]

const TEXT_FIELDS = [
  {
    key: 'consent_prefix',
    label: 'Текст перед названиями документов',
    hint: 'Заканчивается словом, после которого идут названия документов.',
  },
  {
    key: 'user_agreement_title',
    label: 'Название соглашения',
    hint: 'В родительном падеже: «…с условиями Пользовательского соглашения».',
  },
  {
    key: 'privacy_policy_title',
    label: 'Название политики',
    hint: 'Тоже в родительном падеже.',
  },
]

const DOCUMENTS = [
  {
    textKey: 'user_agreement_text',
    titleKey: 'user_agreement_title',
    docKey: 'user_agreement',
    hint: 'Условия использования сервиса',
  },
  {
    textKey: 'privacy_policy_text',
    titleKey: 'privacy_policy_title',
    docKey: 'privacy_policy',
    hint: 'Как обрабатываются данные пользователей',
  },
]

export default function AdminSettingsPage() {
  const { user } = useAuthStore()
  const isOwner = user?.role === 'admin'

  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const tab = TABS.some((t) => t.key === tabParam) ? tabParam : TABS[0].key
  const selectTab = (key) => {
    // replace: переключение вкладок не должно засорять историю —
    // «назад» обязан уводить со страницы настроек, а не листать их.
    setSearchParams(key === TABS[0].key ? {} : { tab: key }, { replace: true })
  }

  const [form, setForm] = useState(null)
  const [initial, setInitial] = useState(null)
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null, defaults: null })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)

  // Открытый редактор документа: ключ текста или null.
  const [editing, setEditing] = useState(null)

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const { data } = await settingsAdminApi.getLegal()
      const { updated_at, updated_by, defaults, ...values } = data.data
      setForm(values)
      setInitial(values)
      setMeta({ updated_at, updated_by, defaults })
    } catch (e) {
      setError(e.response?.data?.message || 'Не удалось загрузить настройки')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isOwner) load()
    else setLoading(false)
  }, [isOwner])

  const dirty = useMemo(
    () => Boolean(form && initial) && JSON.stringify(form) !== JSON.stringify(initial),
    [form, initial]
  )

  const set = (key) => (e) => {
    setSaved(false)
    setForm((f) => ({ ...f, [key]: e.target.value }))
  }

  const problems = useMemo(() => {
    if (!form) return []
    const list = []
    TEXT_FIELDS.forEach((f) => {
      if (!(form[f.key] || '').trim()) list.push(`Поле «${f.label}» не заполнено`)
    })
    DOCUMENTS.forEach((d) => {
      if (!(form[d.textKey] || '').trim())
        list.push(`Текст документа «${form[d.titleKey] || d.docKey}» пуст`)
    })
    return list
  }, [form])

  const save = async () => {
    if (problems.length) return
    setSaving(true)
    setError('')
    try {
      const { data } = await settingsAdminApi.saveLegal(form)
      const { updated_at, updated_by, defaults, ...values } = data.data
      setForm(values)
      setInitial(values)
      setMeta((m) => ({ ...m, updated_at }))
      // Публичные настройки кэшируются на весь сеанс — после правки
      // кэш обязан протухнуть, иначе экран входа в этой же вкладке
      // покажет старый текст.
      resetLegalCache()
      setSaved(true)
    } catch (e) {
      setError(e.response?.data?.message || 'Не удалось сохранить')
    } finally {
      setSaving(false)
    }
  }

  const restoreDefaults = () => {
    if (!meta.defaults) return
    setSaved(false)
    setForm({ ...meta.defaults })
  }

  // Предпросмотр строки согласия открывает документ из текущей формы,
  // а не с сервера: владелец должен видеть несохранённый текст.
  const previewFetch = async (key) => {
    const d = DOCUMENTS.find((x) => x.docKey === key)
    return { title: form?.[d.titleKey], text: form?.[d.textKey] }
  }

  if (!isOwner) {
    return (
      <div className="flex-col gap">
        <PageHeader title="Настройки" />
        <div className="state">
          <Icon name="settings" size={32} />
          <p className="state__title">Раздел доступен владельцу</p>
          <p className="state__text">
            Правовые документы меняет администратор аккаунта.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-col gap">
      <PageHeader
        title="Настройки"
        right={
          tab === 'legal' && dirty ? (
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={save}
              disabled={saving || problems.length > 0}
            >
              {saving ? <span className="spinner spinner--sm" /> : <Icon name="check" size={14} />}
              Сохранить
            </button>
          ) : undefined
        }
      />

      {/* Вкладки разделов. Ходить между ними можно, закрыть — нет:
          это разделы, а не документы, поэтому крестика не бывает. */}
      <div className="settings-tabs" role="tablist" aria-label="Разделы настроек">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={`settings-tab-${t.key}`}
            aria-selected={tab === t.key}
            aria-controls={`settings-panel-${t.key}`}
            tabIndex={tab === t.key ? 0 : -1}
            className={`settings-tabs__tab ${tab === t.key ? 'is-active' : ''}`}
            onClick={() => selectTab(t.key)}
            onKeyDown={(e) => {
              // Стрелки — как в нативных вкладках браузера.
              if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
              e.preventDefault()
              const i = TABS.findIndex((x) => x.key === tab)
              const next = e.key === 'ArrowRight'
                ? TABS[(i + 1) % TABS.length]
                : TABS[(i - 1 + TABS.length) % TABS.length]
              selectTab(next.key)
              document.getElementById(`settings-tab-${next.key}`)?.focus()
            }}
          >
            <Icon name={t.icon} size={15} aria-hidden="true" />
            {t.label}
          </button>
        ))}
      </div>

      <div
        className="settings-tabs__panel"
        role="tabpanel"
        id={`settings-panel-${tab}`}
        aria-labelledby={`settings-tab-${tab}`}
      >
        {tab === 'legal' && (
          <>
            {loading && (
              <div className="flex-col gap-sm">
                <span className="skeleton table__skeleton-row" />
                <span className="skeleton table__skeleton-row" />
                <span className="skeleton table__skeleton-row" />
              </div>
            )}

            {!loading && error && !form && (
              <div className="state">
                <p className="state__title">{error}</p>
                <button type="button" className="btn btn--secondary" onClick={load}>
                  Повторить
                </button>
              </div>
            )}

            {!loading && form && (
              <div className="settings-split">
                <section className="flex-col gap">
                  <div>
                    <h2 className="section-title">Строка согласия</h2>
                    <p className="text-muted" style={{ fontSize: 'var(--text-xs)' }}>
                      Текст показывается под кнопкой на экранах входа и
                      регистрации в обоих приложениях.
                    </p>
                  </div>

                  {TEXT_FIELDS.map((f) => (
                    <div className="field" key={f.key}>
                      <label className="field__label" htmlFor={`legal-${f.key}`}>
                        {f.label}
                      </label>
                      <input
                        id={`legal-${f.key}`}
                        className="input"
                        type="text"
                        value={form[f.key] || ''}
                        onChange={set(f.key)}
                        aria-invalid={!(form[f.key] || '').trim()}
                      />
                      {f.hint && <span className="field__hint">{f.hint}</span>}
                    </div>
                  ))}

                  <div>
                    <h2 className="section-title">Тексты документов</h2>
                    <p className="text-muted" style={{ fontSize: 'var(--text-xs)' }}>
                      Хранятся в системе и открываются внутри приложения.
                      Пользователь читает ровно этот текст, когда нажимает
                      название документа.
                    </p>
                  </div>

                  {DOCUMENTS.map((d) => {
                    const text = form[d.textKey] || ''
                    const empty = !text.trim()
                    return (
                      <div className="legal-doc-row" key={d.textKey}>
                        <Icon name="file" size={20} className="legal-doc-row__icon" />
                        <div className="legal-doc-row__text">
                          <span className="legal-doc-row__title">
                            {form[d.titleKey] || d.docKey}
                          </span>
                          <span className="legal-doc-row__meta">
                            {empty
                              ? 'Текст не заполнен'
                              : `${text.trim().length.toLocaleString('ru-RU')} знаков · ${d.hint}`}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn btn--secondary btn--sm"
                          onClick={() => setEditing(d.textKey)}
                          aria-label={`Редактировать: ${form[d.titleKey] || d.docKey}`}
                        >
                          <Icon name="edit" size={14} />
                          Редактировать
                        </button>
                      </div>
                    )
                  })}

                  <label className="settings__row">
                    <span className="settings__text">
                      <span className="settings__label">Показывать уведомление</span>
                      <span className="settings__hint">
                        Выключать стоит только если согласие собирается другим
                        способом — иначе пользователь не увидит, с чем соглашается.
                      </span>
                    </span>
                    <input
                      type="checkbox"
                      className="switch"
                      checked={form.enabled !== false}
                      onChange={(e) => {
                        setSaved(false)
                        setForm((f) => ({ ...f, enabled: e.target.checked }))
                      }}
                    />
                  </label>

                  {problems.length > 0 && (
                    <div className="alert alert--error">
                      <ul style={{ margin: 0, paddingLeft: '1.1em' }}>
                        {problems.map((p) => (
                          <li key={p}>{p}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {error && <div className="alert alert--error">{error}</div>}
                  {saved && !dirty && (
                    <div className="alert alert--success">Настройки сохранены</div>
                  )}

                  <div className="flex-row gap-sm" style={{ flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn btn--primary"
                      onClick={save}
                      disabled={saving || !dirty || problems.length > 0}
                    >
                      {saving ? <span className="spinner spinner--sm" /> : null}
                      Сохранить
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => setForm({ ...initial })}
                      disabled={!dirty || saving}
                    >
                      Отменить
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={restoreDefaults}
                      disabled={saving || !meta.defaults}
                    >
                      Вернуть значения по умолчанию
                    </button>
                  </div>
                </section>

                <aside className="card card--flat flex-col gap">
                  <h2 className="section-title">Как это увидит пользователь</h2>

                  <div className="settings-preview">
                    <button type="button" className="btn btn--primary btn--full" disabled>
                      Войти
                    </button>
                    <LegalConsent legal={form} fetchDocument={previewFetch} />
                  </div>

                  <p className="text-muted" style={{ fontSize: 'var(--text-xs)' }}>
                    Названия документов открывают текст прямо в приложении —
                    в предпросмотре показывается несохранённая версия.
                  </p>

                  {meta.updated_at && (
                    <p className="text-muted" style={{ fontSize: 'var(--text-xs)' }}>
                      Изменено {formatDateTime(meta.updated_at)}
                      {meta.updated_by ? `, ${meta.updated_by.full_name}` : ''}. Правки
                      записываются в журнал.
                    </p>
                  )}
                </aside>
              </div>
            )}
          </>
        )}

        {tab === 'video' && <VideoSettingsSection />}
      </div>

      {editing && form && (
        <LegalDocEditor
          title={form[DOCUMENTS.find((d) => d.textKey === editing)?.titleKey] || 'Документ'}
          value={form[editing] || ''}
          onCancel={() => setEditing(null)}
          onApply={(text) => {
            setSaved(false)
            setForm((f) => ({ ...f, [editing]: text }))
            setEditing(null)
          }}
        />
      )}
    </div>
  )
}

/**
 * Окно правки текста документа: слева Markdown-исходник, справа то,
 * что увидит читатель, — как в редакторе базы знаний. Предпросмотр
 * рядом, а не по кнопке: юридический текст с заголовками набирают,
 * глядя на результат.
 *
 * «Готово» лишь кладёт текст в форму — сохраняет общая кнопка
 * страницы, одним PUT со строкой согласия. Иначе у документа
 * и остальных полей были бы разные моменты сохранения и разная
 * запись в журнале.
 */
function LegalDocEditor({ title, value, onCancel, onApply }) {
  const [text, setText] = useState(value)
  const [pane, setPane] = useState('edit') // мобильный режим: edit | preview
  const textareaRef = useRef(null)

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  const empty = !text.trim()

  return (
    <div className="modal-overlay" onClick={onCancel} role="presentation">
      <div
        className="modal legal-editor"
        role="dialog"
        aria-modal="true"
        aria-label={`Редактирование: ${title}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal__head">
          <h2 className="modal__title">{title}</h2>
          <button
            type="button"
            className="modal__close"
            onClick={onCancel}
            aria-label="Закрыть без изменений"
          >
            <Icon name="close" size={18} />
          </button>
        </header>

        {/* На узком экране обе панели не помещаются — переключатель. */}
        <div className="legal-editor__panes-switch">
          {[['edit', 'Текст'], ['preview', 'Предпросмотр']].map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`legal-editor__pane-btn ${pane === key ? 'is-active' : ''}`}
              onClick={() => setPane(key)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className={`legal-editor__body legal-editor__body--${pane}`}>
          <div className="legal-editor__source">
            <MarkdownToolbar textareaRef={textareaRef} onChange={setText} />
            <textarea
              ref={textareaRef}
              className="legal-editor__textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => handleToolbarKeydown(e, textareaRef, setText)}
              spellCheck="true"
              aria-label="Текст документа в формате Markdown"
              autoFocus
            />
            <span className="legal-editor__meta">
              Markdown: # заголовки, **жирный**, списки ·{' '}
              {text.trim().length.toLocaleString('ru-RU')} знаков
            </span>
          </div>
          <div className="legal-editor__preview">
            <Markdown text={text} onTasksChange={setText} />
          </div>
        </div>

        <footer className="modal__foot">
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            Отменить
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => onApply(text)}
            disabled={empty}
            title={empty ? 'Текст документа не может быть пустым' : undefined}
          >
            <Icon name="check" size={14} />
            Готово
          </button>
        </footer>
      </div>
    </div>
  )
}
